import React, { useState, useContext, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";
import { AuthContext } from "../Hook/AuthProvider";
import { ROUTES } from "../constants/routes";
import Audioplayer from "./Audioplayer";
import PromoDrawer from "./PromoDrawer";
import { getHistory } from "../services/getHistory";
import syllabusSubjectsData from "../data/syllabusSubjects.json";
import {
  fetchTargets,
  createTarget,
  updateTarget,
  deleteTarget,
  fetchHealthMetrics,
  fetchSyllabusProgress,
  fetchMilestones,
  fetchRevisionWeakness,
} from "../services/dashboardService";
import {
  Clock,
  FileText,
  Headphones,
  Play,
  CheckCircle2,
  Circle,
  X,
  Upload,
  Target,
  TrendingUp,
  AlertCircle,
  ChevronRight,
  Plus,
  Trash2,
  AlertTriangle,
  ChevronDown,
  ExternalLink,
  Newspaper,
  PenTool,
  Activity,
  MessageSquarePlus,
  RotateCcw,
  ShieldAlert,
  Sparkles,
  BookOpen,
} from "lucide-react";

// 5 colors from Green (>= 70%) to Grey (< 10%) based on completion
const BASE_TREEMAP_PALETTE = [
  "#16a34a", // 1. >= 70%: Green
  "#22c55e", // 2. 50% - 69%: Soft Green
  "#4a7c59", // 3. 30% - 49%: Sage / Olive Green-Grey
  "#64748b", // 4. 10% - 29%: Slate Grey
  "#475569", // 5. < 10%: Neutral Grey
];

const getTreemapColor = (completionPercent) => {
  const comp = Number(completionPercent) || 0;
  if (comp >= 70) return BASE_TREEMAP_PALETTE[0]; // Green (70% and above)
  if (comp >= 50) return BASE_TREEMAP_PALETTE[1]; // 50% - 69%
  if (comp >= 30) return BASE_TREEMAP_PALETTE[2]; // 30% - 49%
  if (comp >= 10) return BASE_TREEMAP_PALETTE[3]; // 10% - 29%
  return BASE_TREEMAP_PALETTE[4];                 // Grey (< 10%)
};

// Helper to sanitize any legacy mock target tasks from localStorage
const sanitizeLoadedTasks = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const mockIds = new Set(["t1", "t2", "t3", "w1", "w2", "w3", "w4"]);
      return parsed.filter((t) => t && !mockIds.has(String(t.id)));
    }
    return [];
  } catch {
    return [];
  }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { profileData } = useContext(AuthContext) || {};

  // Action states
  const [showAudio, setShowAudio] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingFileName, setPendingFileName] = useState("");
  const [evalQuestion, setEvalQuestion] = useState("");
  const [evalSubject, setEvalSubject] = useState("GS");
  const [evalMarks, setEvalMarks] = useState("10");
  const fileInputRef = useRef(null);

  // Target scope: "today" vs "week"
  const [targetScope, setTargetScope] = useState("today");

  // Custom User Targets (initialized to [] or sanitized user-created targets)
  const [todayTasks, setTodayTasks] = useState(() => {
    try {
      const saved = localStorage.getItem("yooki_dashboard_tasks_today");
      return saved ? sanitizeLoadedTasks(saved) : [];
    } catch {
      return [];
    }
  });

  const [weekTasks, setWeekTasks] = useState(() => {
    try {
      const saved = localStorage.getItem("yooki_dashboard_tasks_week");
      return saved ? sanitizeLoadedTasks(saved) : [];
    } catch {
      return [];
    }
  });

  // Task adding state
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskTag, setNewTaskTag] = useState("Study");

  // Inactivity alert state (check if user was inactive for X days)
  const [showInactivityAlert, setShowInactivityAlert] = useState(() => {
    try {
      const dismissed = localStorage.getItem("yooki_inactivity_dismissed");
      return !dismissed;
    } catch {
      return true;
    }
  });
  const [inactiveDays] = useState(3);

  // 50% split on initial landing (user can resize horizontally)
  const [leftPercent, setLeftPercent] = useState(() => {
    try {
      const saved = localStorage.getItem("yooki_dashboard_split_ratio");
      return saved ? parseFloat(saved) : 50;
    } catch {
      return 50;
    }
  });
  const isDraggingRef = useRef(false);
  const containerRef = useRef(null);

  // Layer 3 Syllabus split: default 60%, capped at 70% max
  const [syllabusSplitPercent, setSyllabusSplitPercent] = useState(() => {
    try {
      const saved = localStorage.getItem("yooki_syllabus_split_ratio");
      return saved ? parseFloat(saved) : 60;
    } catch {
      return 60;
    }
  });
  const isDraggingSyllabusRef = useRef(false);
  const syllabusContainerRef = useRef(null);

  // Fetch user targets from backend on mount and scope changes
  useEffect(() => {
    // Purge legacy mock task entries from localStorage if present
    const cleanStorage = (key) => {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const sanitized = sanitizeLoadedTasks(item);
          if (sanitized.length > 0) {
            localStorage.setItem(key, JSON.stringify(sanitized));
          } else {
            localStorage.removeItem(key);
          }
        }
      } catch { }
    };
    cleanStorage("yooki_dashboard_tasks_today");
    cleanStorage("yooki_dashboard_tasks_week");

    let isMounted = true;
    const loadTargets = async (scope) => {
      try {
        const res = await fetchTargets({ scope });
        if (isMounted && res && Array.isArray(res.targets)) {
          if (scope === "today") {
            setTodayTasks(res.targets);
            localStorage.setItem("yooki_dashboard_tasks_today", JSON.stringify(res.targets));
          } else {
            setWeekTasks(res.targets);
            localStorage.setItem("yooki_dashboard_tasks_week", JSON.stringify(res.targets));
          }
        }
      } catch (err) {
        console.warn(`Could not fetch ${scope} targets from backend, fallback to local:`, err);
      }
    };

    loadTargets("today");
    loadTargets("week");

    return () => {
      isMounted = false;
    };
  }, []);

  // Health Metrics from backend
  const [healthMetrics, setHealthMetrics] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadHealthMetrics = async () => {
      try {
        const res = await fetchHealthMetrics();
        if (isMounted && res && res.success) {
          setHealthMetrics(res);
        }
      } catch (err) {
        console.warn("Could not fetch health metrics from backend, using fallback presentation:", err);
      }
    };

    loadHealthMetrics();

    return () => {
      isMounted = false;
    };
  }, []);

  // Syllabus Progress data from backend
  const [syllabusProgressData, setSyllabusProgressData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadSyllabusProgress = async () => {
      try {
        const res = await fetchSyllabusProgress();
        if (isMounted && res && res.success) {
          setSyllabusProgressData(res);
        }
      } catch (err) {
        console.warn("Could not fetch syllabus progress from backend, using fallback presentation:", err);
      }
    };

    loadSyllabusProgress();

    return () => {
      isMounted = false;
    };
  }, []);

  // Lifetime Milestone Counters from backend
  const [milestonesData, setMilestonesData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadMilestones = async () => {
      try {
        const res = await fetchMilestones();
        if (isMounted && res && res.success) {
          setMilestonesData(res);
        }
      } catch (err) {
        console.warn("Could not fetch milestone counters from backend, using fallback presentation:", err);
      }
    };

    loadMilestones();

    return () => {
      isMounted = false;
    };
  }, []);

  // Revision Queue & Weakness Spotlight state
  const [revisionWeaknessData, setRevisionWeaknessData] = useState(null);
  const [activeKpiTab, setActiveKpiTab] = useState("revision"); // "revision" | "weakness"

  useEffect(() => {
    let isMounted = true;
    const loadRevisionWeakness = async () => {
      try {
        const res = await fetchRevisionWeakness();
        if (isMounted && res && res.success) {
          setRevisionWeaknessData(res);
        }
      } catch (err) {
        console.warn("Could not fetch revision & weakness data:", err);
      }
    };

    loadRevisionWeakness();

    return () => {
      isMounted = false;
    };
  }, []);


  // Resizer drag handler (Layer 1 Mission/Contributions & Layer 3 Syllabus)
  useEffect(() => {
    const handleMouseMove = (e) => {
      // Layer 1 drag
      if (isDraggingRef.current && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const rawPercent = ((e.clientX - rect.left) / rect.width) * 100;
        const clamped = Math.max(28, Math.min(72, rawPercent));
        setLeftPercent(clamped);
        try {
          localStorage.setItem("yooki_dashboard_split_ratio", clamped.toString());
        } catch (err) {
          // ignore
        }
      }

      // Layer 3 Syllabus drag: default 60%, capped at 70% max
      if (isDraggingSyllabusRef.current && syllabusContainerRef.current) {
        const rect = syllabusContainerRef.current.getBoundingClientRect();
        const rawPercent = ((e.clientX - rect.left) / rect.width) * 100;
        // Cap at 70% max, min 35%
        const clamped = Math.max(35, Math.min(70, rawPercent));
        setSyllabusSplitPercent(clamped);
        try {
          localStorage.setItem("yooki_syllabus_split_ratio", clamped.toString());
        } catch (err) {
          // ignore
        }
      }
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      isDraggingSyllabusRef.current = false;
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDraggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleSyllabusMouseDown = (e) => {
    e.preventDefault();
    isDraggingSyllabusRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // Current task list based on scope
  const currentTasks = targetScope === "today" ? todayTasks : weekTasks;
  const setCurrentTasks = targetScope === "today" ? setTodayTasks : setWeekTasks;

  const toggleTask = async (id) => {
    const task = currentTasks.find((t) => t.id === id);
    if (!task) return;
    const newStatus = !task.completed;

    // Optimistic UI update
    setCurrentTasks((prev) =>
      prev.map((t) => (t.id === id ? { ...t, completed: newStatus } : t))
    );

    // Call backend if it's a backend target (numeric ID or valid backend ID)
    if (typeof id === "number" || (!String(id).startsWith("t") && !String(id).startsWith("w") && !String(id).startsWith("task_"))) {
      try {
        await updateTarget(id, { completed: newStatus });
      } catch (err) {
        console.error("Failed to update target status on backend:", err);
      }
    }
  };

  const deleteTask = async (id, e) => {
    e.stopPropagation();
    // Optimistic UI update
    setCurrentTasks((prev) => prev.filter((t) => t.id !== id));

    if (typeof id === "number" || (!String(id).startsWith("t") && !String(id).startsWith("w") && !String(id).startsWith("task_"))) {
      try {
        await deleteTarget(id);
      } catch (err) {
        console.error("Failed to delete target on backend:", err);
      }
    }
  };

  const handleAddNewTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    const title = newTaskTitle.trim();
    const tag = newTaskTag;
    setNewTaskTitle("");
    setIsAddingTask(false);

    try {
      const res = await createTarget({
        title,
        tag,
        scope: targetScope,
      });
      if (res && res.target) {
        setCurrentTasks((prev) => [...prev, res.target]);
        return;
      }
    } catch (err) {
      console.error("Failed to create target on backend, using local fallback:", err);
    }

    // Local fallback
    const newTask = {
      id: "task_" + Date.now(),
      title,
      completed: false,
      tag,
      scope: targetScope,
    };
    setCurrentTasks((prev) => [...prev, newTask]);
  };

  const dismissInactivityAlert = () => {
    setShowInactivityAlert(false);
    try {
      localStorage.setItem("yooki_inactivity_dismissed", "true");
    } catch { }
  };

  const completedCount = currentTasks.filter((t) => t.completed).length;

  // Dynamic Session Action Button: Check user chat history state on dashboard load
  const [hasChatHistory, setHasChatHistory] = useState(false);
  const [latestSession, setLatestSession] = useState(null);
  const [resumingSession, setResumingSession] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const checkUserHistory = async () => {
      try {
        const response = await getHistory(1, 5);
        let list = [];
        if (response) {
          if (Array.isArray(response)) {
            list = response;
          } else if (typeof response === "object") {
            Object.values(response).forEach((group) => {
              if (Array.isArray(group)) {
                list.push(...group);
              }
            });
          }
        }
        if (isMounted) {
          if (list.length > 0) {
            setHasChatHistory(true);
            setLatestSession(list[0]);
          } else {
            setHasChatHistory(false);
            setLatestSession(null);
          }
        }
      } catch (err) {
        if (isMounted) {
          setHasChatHistory(false);
          setLatestSession(null);
        }
      }
    };

    checkUserHistory();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSessionAction = async () => {
    if (!hasChatHistory) {
      navigate(ROUTES.CHAT || "/chat");
      return;
    }

    setResumingSession(true);
    try {
      let target = latestSession;
      if (!target) {
        const response = await getHistory(1, 5);
        let list = [];
        if (response) {
          if (Array.isArray(response)) {
            list = response;
          } else if (typeof response === "object") {
            Object.values(response).forEach((group) => {
              if (Array.isArray(group)) {
                list.push(...group);
              }
            });
          }
        }
        if (list.length > 0) target = list[0];
      }

      if (target) {
        const messageId = target.message_id || target.last_message_id;
        const convId = target.conversation_id || target.id;
        if (messageId) {
          navigate(`${ROUTES.CHAT}/message/${messageId}`);
        } else if (convId) {
          navigate(`${ROUTES.CHAT}/${convId}`);
        } else {
          navigate(ROUTES.CHAT);
        }
      } else {
        navigate(ROUTES.CHAT);
      }
    } catch (err) {
      console.error("Failed to resume previous session:", err);
      navigate(ROUTES.CHAT);
    } finally {
      setResumingSession(false);
    }
  };


  const [trackedSubjects, setTrackedSubjects] = useState(() => {
    try {
      const saved = localStorage.getItem("yooki_tracked_syllabus_subjects");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed.slice(0, 5);
      }
    } catch { }
    return ["Polity", "History", "Economy", "Geography", "Ethics"];
  });

  const [selectedSubjectKey, setSelectedSubjectKey] = useState(() => {
    return trackedSubjects[0] || "Polity";
  });

  // Sync tracked subjects to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("yooki_tracked_syllabus_subjects", JSON.stringify(trackedSubjects));
    } catch (e) {
      console.error(e);
    }
  }, [trackedSubjects]);

  // Remove a subject from tracking
  const handleRemoveSubject = (subjectToRemove, e) => {
    e.stopPropagation();
    if (trackedSubjects.length <= 1) {
      alert("At least one subject must remain tracked in your syllabus progress.");
      return;
    }
    const updated = trackedSubjects.filter((s) => s !== subjectToRemove);
    setTrackedSubjects(updated);
    if (selectedSubjectKey === subjectToRemove) {
      setSelectedSubjectKey(updated[0] || "Polity");
    }
  };

  // Available untracked subjects from mtr_subjects metadata, alphabetically sorted
  const availableSubjects = useMemo(() => {
    return syllabusSubjectsData
      .filter((s) => !trackedSubjects.some((t) => t.toLowerCase() === s.name.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [trackedSubjects]);

  const isMaxSubjectsReached = trackedSubjects.length >= 5;

  const handleSelectSubject = (subjectName) => {
    if (trackedSubjects.length >= 5) {
      alert("Maximum 5 subjects can be tracked on the dashboard.");
      return;
    }
    if (!trackedSubjects.includes(subjectName)) {
      setTrackedSubjects((prev) => [...prev, subjectName].slice(0, 5));
      setSelectedSubjectKey(subjectName);
    }
  };

  // Real-time Countdown calculation to UPSC Prelims 2027: 23 May 2027
  const prelimsDate2027 = useMemo(() => new Date(2027, 4, 23, 9, 30, 0), []);
  const [countdown, setCountdown] = useState(() => {
    const now = new Date();
    const diffMs = new Date(2027, 4, 23, 9, 30, 0) - now;
    if (diffMs > 0) {
      const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const weeks = Math.floor(totalDays / 7);
      return { days: totalDays, hours, weeks };
    }
    return { days: 0, hours: 0, weeks: 0 };
  });

  // Dynamic card ordering based on upcoming exam
  const isPrelimsUpcoming = useMemo(() => {
    const now = new Date();
    return now <= prelimsDate2027;
  }, [prelimsDate2027]);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const diffMs = prelimsDate2027 - now;
      if (diffMs > 0) {
        const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const weeks = Math.floor(totalDays / 7);
        setCountdown({ days: totalDays, hours, weeks });
      } else {
        setCountdown({ days: 0, hours: 0, weeks: 0 });
      }
    };
    const timer = setInterval(updateCountdown, 60000);
    return () => clearInterval(timer);
  }, [prelimsDate2027]);

  // Dynamic Greeting & User Name
  const rawUserName = profileData?.first_name;
  const firstName = (rawUserName || "").trim().split(" ")[0] || "";
  const userName = firstName ? firstName[0].toUpperCase() + firstName.slice(1).toLowerCase() : "Aspirant";
  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? "Good Morning" : currentHour < 17 ? "Good Afternoon" : "Good Evening";

  // Evaluation modal options
  const evalMarksOptions = {
    GS: ["10", "15"],
    Ethics: ["10", "20"],
    Essay: ["125"],
    Optional: ["10", "15", "20"],
  };

  const openUploadModal = () => {
    setPendingFile(null);
    setPendingFileName("");
    setEvalQuestion("");
    setEvalSubject("GS");
    setEvalMarks("10");
    setShowUploadModal(true);
  };

  const handleEvalFile = (file) => {
    if (!file) return;
    setPendingFile(file);
    setPendingFileName(file.name);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleEvalFile(e.dataTransfer.files[0]);
  };

  const handleEvaluateClick = () => {
    if (!pendingFile || !evalQuestion?.trim()) return;
    setShowUploadModal(false);
    const evalutedata = [
      {
        sender: "user",
        message: evalQuestion.trim(),
        fildetail: pendingFile,
        attachmark: evalMarks,
        attchsubject: evalSubject,
        prompt: "evaluate",
        mcqData: null,
        mainsPractice: false,
      },
    ];
    navigate(ROUTES.CHAT, {
      state: {
        evalutedata,
        evaluate: true,
        flag: Math.floor(Math.random() * 1000000),
      },
    });
  };


  const activeSyllabus = useMemo(() => {
    if (syllabusProgressData && Array.isArray(syllabusProgressData.subjects)) {
      const cleanKey = (selectedSubjectKey || "").trim().toLowerCase();
      const matchedSubject = syllabusProgressData.subjects.find(
        (s) =>
          s.subject_name.toLowerCase() === cleanKey ||
          s.subject_name.toLowerCase().includes(cleanKey) ||
          cleanKey.includes(s.subject_name.toLowerCase())
      );

      if (matchedSubject) {
        const matchingTopics = (syllabusProgressData.topics || []).filter(
          (t) => t.subject_id === matchedSubject.subject_id
        );

        const priorityOrder = { High: 1, Medium: 2, Low: 3 };

        const sortedTopics = [...matchingTopics].sort((a, b) => {
          const aComp = (a.completed_subtopics > 0 && a.completed_subtopics === a.total_subtopics) || (a.completion_percent || 0) >= 100;
          const bComp = (b.completed_subtopics > 0 && b.completed_subtopics === b.total_subtopics) || (b.completion_percent || 0) >= 100;

          if (aComp && !bComp) return -1;
          if (!aComp && bComp) return 1;

          const pA = priorityOrder[a.priority] || 4;
          const pB = priorityOrder[b.priority] || 4;
          if (pA !== pB) return pA - pB;

          const orderA = a.disp_order ?? 0;
          const orderB = b.disp_order ?? 0;
          if (orderA !== orderB) return orderA - orderB;

          return (a.topic_id ?? 0) - (b.topic_id ?? 0);
        });

        const subtopics = sortedTopics.length > 0
          ? sortedTopics.slice(0, 8).map((t) => {
            const comp = Math.round(t.completion_percent || 0);
            const isCompleted = (t.completed_subtopics > 0 && t.completed_subtopics === t.total_subtopics) || comp >= 100;

            let status = "grey";
            let statusText = "Not Started";
            if (isCompleted) {
              status = "green";
              statusText = "Completed";
            } else if (comp >= 75) {
              status = "green";
              statusText = "Mastered";
            } else if (comp >= 35) {
              status = "amber";
              statusText = "In Progress";
            } else if (comp > 0) {
              status = "red";
              statusText = "Needs Focus";
            }

            const formatDate = (val) => {
              if (!val) return null;
              try {
                const dateObj = new Date(val);
                if (!isNaN(dateObj.getTime())) {
                  return dateObj.toLocaleDateString("en-US", {
                    month: "short",
                    day: "2-digit",
                    year: "numeric",
                  });
                }
                return val;
              } catch (_) {
                return val;
              }
            };

            const formattedCompletedOn = formatDate(t.completed_on);
            const formattedStartedOn = formatDate(t.started_on);

            return {
              id: `topic_${t.topic_id}`,
              name: t.topic_name,
              completed: comp,
              status,
              statusText,
              completedOn: formattedCompletedOn,
              startedOn: formattedStartedOn,
              isCompleted,
            };
          })
          : [];

        const staticMeta = syllabusSubjectsData.find(
          (s) =>
            s.name.toLowerCase() === cleanKey ||
            s.name.toLowerCase().includes(cleanKey) ||
            cleanKey.includes(s.name.toLowerCase())
        );
        const paperTag = staticMeta?.paper || "General Studies";

        const compPct = Math.round(matchedSubject.completion_percent || 0);
        return {
          subject: matchedSubject.subject_name,
          paper: paperTag,
          completedPercent: compPct,
          remainingPercent: Math.max(0, 100 - compPct),
          subtopics,
        };
      }
    }

    const cleanKey = (selectedSubjectKey || "").trim().toLowerCase();
    const staticMeta = syllabusSubjectsData.find(
      (s) =>
        s.name.toLowerCase() === cleanKey ||
        s.name.toLowerCase().includes(cleanKey) ||
        cleanKey.includes(s.name.toLowerCase())
    );
    return {
      subject: selectedSubjectKey || "Polity",
      paper: staticMeta?.paper || "General Studies",
      completedPercent: 0,
      remainingPercent: 100,
      subtopics: [],
    };
  }, [syllabusProgressData, selectedSubjectKey]);

  return (
    <div className="dashboard-modern-wrapper">
      <div className="dashboard-modern-container">

        {/* ========================================================================= */}
        {/* TOP ROW: 50/50 ON LANDING, USER RESIZABLE SPLIT TILES                     */}
        {/* ========================================================================= */}
        <div
          className="dashboard-top-anchor-grid"
          ref={containerRef}
          style={{
            gridTemplateColumns: `calc(${leftPercent}% - 8px) 16px 1fr`,
          }}
        >

          {/* Left Tile: DAILY MISSION */}
          <div className="today-focus-card">

            {/* Inactivity Alert Banner (Disabled) */}
            {/* {showInactivityAlert && (
              <div className="inactivity-alert-banner">
                <div className="inactivity-alert-left">
                  <AlertTriangle size={15} className="inactivity-icon" />
                  <span>
                    No activity logged for <strong>{inactiveDays} days</strong>. Let's restart your momentum today!
                  </span>
                </div>
                <button
                  className="inactivity-dismiss-btn"
                  onClick={dismissInactivityAlert}
                  title="Dismiss alert"
                >
                  <X size={13} />
                </button>
              </div>
            )} */}

            <div className="today-focus-header">
              <div className="today-greeting-box">
                <span className="today-badge">
                  {targetScope === "today" ? "DAILY MISSION" : "WEEKLY MISSION"}
                </span>
                <h2 className="today-greeting-title">
                  {greeting}, <span className="greeting-name">{userName}</span>
                </h2>

                {/* Target Today | Week Tab Toggle */}
                <div className="target-scope-toggle-bar">
                  <span className="target-scope-label">Target:</span>
                  <button
                    className={`target-scope-btn ${targetScope === "today" ? "active" : ""}`}
                    onClick={() => setTargetScope("today")}
                  >
                    Today
                  </button>
                  <span className="target-scope-pipe">|</span>
                  <button
                    className={`target-scope-btn ${targetScope === "week" ? "active" : ""}`}
                    onClick={() => setTargetScope("week")}
                  >
                    Week
                  </button>
                </div>
              </div>

              <div className={`today-status-chip ${currentTasks.length === 0 ? "grey" : completedCount === currentTasks.length ? "green" : "amber"}`}>
                <CheckCircle2 size={14} />
                <span>{currentTasks.length === 0 ? "0 Targets Set" : `${completedCount} of ${currentTasks.length} Done`}</span>
              </div>
            </div>

            {/* Target Checklist with User Entry Capabilities */}
            <div className="daily-tasks-strip">
              {currentTasks.length === 0 ? (
                <div className="dashboard-empty-strip-msg">
                  <span>No targets set for {targetScope === "today" ? "today" : "this week"}. Click "+ Add Target / Mentor Task" below to set your goals.</span>
                </div>
              ) : (
                currentTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`task-row-item ${task.completed ? "completed" : ""}`}
                    onClick={() => toggleTask(task.id)}
                  >
                    <div className="task-checkbox-wrap">
                      {task.completed ? (
                        <CheckCircle2 size={16} className="task-check-icon checked" />
                      ) : (
                        <Circle size={16} className="task-check-icon unchecked" />
                      )}
                    </div>
                    <span className="task-row-title">{task.title}</span>
                    <span className="task-tag-pill">{task.tag}</span>
                    <button
                      className="task-delete-btn"
                      onClick={(e) => deleteTask(task.id, e)}
                      title="Remove target"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Add Custom Target Input Form */}
            {isAddingTask ? (
              <form className="add-task-form" onSubmit={handleAddNewTask}>
                <input
                  type="text"
                  placeholder={`Enter ${targetScope === "today" ? "today's" : "this week's"} study or practice goal...`}
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  autoFocus
                  className="add-task-input"
                />
                <div className="add-task-controls">
                  <select
                    value={newTaskTag}
                    onChange={(e) => setNewTaskTag(e.target.value)}
                    className="add-task-tag-select"
                  >
                    <option value="Study">Study</option>
                    <option value="Practice">Practice</option>
                    <option value="Revision">Revision</option>
                    <option value="Prelims">Prelims</option>
                    <option value="Mains">Mains</option>
                    <option value="Mentor Task">Mentor Task</option>
                  </select>
                  <button type="submit" className="btn-save-task">
                    Save
                  </button>
                  <button
                    type="button"
                    className="btn-cancel-task"
                    onClick={() => {
                      setIsAddingTask(false);
                      setNewTaskTitle("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                className="btn-trigger-add-task"
                onClick={() => setIsAddingTask(true)}
              >
                <Plus size={14} />
                <span>Add Target / Mentor Task</span>
              </button>
            )}

            {/* Session Action Button & View History */}
            <div className="today-actions-bar">
              <button
                className="btn-resume-session"
                onClick={handleSessionAction}
                disabled={resumingSession}
                title={hasChatHistory ? "Resume your latest chat session" : "Start a new chat session"}
              >
                {hasChatHistory ? (
                  <Play size={15} fill="currentColor" />
                ) : (
                  <MessageSquarePlus size={15} />
                )}
                <span>
                  {resumingSession
                    ? "Resuming..."
                    : hasChatHistory
                      ? "Resume Previous Session"
                      : "Start New Session"}
                </span>
              </button>
              <button
                className="btn-open-history"
                onClick={() => navigate(ROUTES.HISTORY || "/history")}
              >
                <span>View History</span>
                <ChevronRight size={15} />
              </button>
            </div>
          </div>

          {/* Draggable Split Resizer Handle */}
          <div
            className="split-resizer-handle"
            onMouseDown={handleMouseDown}
            title="Click and drag to resize tiles"
          >
            <div className="resizer-dots-pill">
              <span className="resizer-dot" />
              <span className="resizer-dot" />
              <span className="resizer-dot" />
            </div>
          </div>

          {/* Right Tile: Parent Div with UPSC TARGET + Study Contribution Vertically */}
          <div className="top-right-column-container">

            {/* Card 1: UPSC PRELIMS 2027 TARGET */}
            <div className="upsc-target-banner-card">
              <div className="upsc-target-left">
                <div className="target-icon-badge">
                  <Target size={18} />
                </div>
                <div className="target-meta">
                  <span className="target-label">UPSC PRELIMS 2027 TARGET</span>
                  <span className="target-date">23 May 2027</span>
                </div>
              </div>

              <div className="upsc-target-right">
                <div className="countdown-pill primary">
                  <strong>{countdown.days}</strong> Days Left
                </div>
                <div className="countdown-pill">
                  <strong>{countdown.weeks}</strong> Weeks
                </div>
                <span className="phase-badge">Foundation Phase</span>
              </div>
            </div>

            {/* Card 2: Spaced Revision & Weakness Spotlight Card */}
            <div className="study-contributions-card revision-weakness-card">
              <div className="study-contributions-header">
                {/* Dual Toggle Tabs */}
                <div className="kpi-tab-switch-group">
                  <button
                    className={`kpi-tab-switch-btn ${activeKpiTab === "revision" ? "active" : ""}`}
                    onClick={() => setActiveKpiTab("revision")}
                  >
                    <RotateCcw size={12} />
                    <span>REVISION QUEUE</span>
                    {revisionWeaknessData?.revision?.items?.length > 0 && (
                      <span className="kpi-tab-badge amber">
                        {revisionWeaknessData.revision.items.length}
                      </span>
                    )}
                  </button>

                  <button
                    className={`kpi-tab-switch-btn ${activeKpiTab === "weakness" ? "active" : ""}`}
                    onClick={() => setActiveKpiTab("weakness")}
                  >
                    <ShieldAlert size={12} />
                    <span>WEAK AREAS</span>
                    {revisionWeaknessData?.weak_areas?.length > 0 && (
                      <span className="kpi-tab-badge red">
                        {revisionWeaknessData.weak_areas.length}
                      </span>
                    )}
                  </button>
                </div>

                {/* Header Stats */}
                <div className="study-header-stats">
                  {!revisionWeaknessData ? (
                    <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "90px", height: "18px" }} />
                  ) : activeKpiTab === "revision" ? (
                    <span className="study-total-pill">
                      Retention: <strong>
                        {revisionWeaknessData.revision.total_completed === 0 || revisionWeaknessData.revision.retention_index === null || revisionWeaknessData.revision.retention_index === undefined
                          ? "NA"
                          : `${revisionWeaknessData.revision.retention_index}%`}
                      </strong>
                    </span>
                  ) : (
                    <span className="study-sessions-pill">
                      Accuracy: <strong>{revisionWeaknessData.weak_areas.overall_accuracy}%</strong> ({revisionWeaknessData.weak_areas.total_solved} MCQs)
                    </span>
                  )}
                </div>
              </div>

              {/* Tab 1: Revision Queue Content */}
              {activeKpiTab === "revision" && (
                <div className="kpi-tab-body">
                  {!revisionWeaknessData ? (
                    <div className="dashboard-skeleton dashboard-skeleton-box" style={{ width: "100%", height: "100%", minHeight: "140px", borderRadius: "8px" }} />
                  ) : revisionWeaknessData.revision.items.length === 0 ? (
                    revisionWeaknessData.revision.total_completed === 0 ? (
                      <div className="dashboard-empty-state-card compact">
                        <BookOpen size={20} className="dashboard-empty-icon text-blue" />
                        <span className="dashboard-empty-title">Begin Your Preparation Journey</span>
                        <span className="dashboard-empty-sub">
                          Every ranker starts with day one. Complete your first topic in the syllabus!
                        </span>
                      </div>
                    ) : (
                      <div className="dashboard-empty-state-card compact">
                        <Sparkles size={20} className="dashboard-empty-icon text-green" />
                        <span className="dashboard-empty-title">All Topics Up to Date</span>
                        <span className="dashboard-empty-sub">No topics due for revision right now. Great retention pace!</span>
                      </div>
                    )
                  ) : (
                    <div className="kpi-items-scrollable">
                      {revisionWeaknessData.revision.items.map((item) => (
                        <div key={item.subtopic_id} className="kpi-list-row revision-row">
                          <div className="kpi-row-info">
                            <div className="kpi-row-line-1">
                              <span className="kpi-row-main-title" title={item.subtopic_name}>
                                {item.subtopic_name}
                              </span>
                              <span className={`kpi-decay-chip ${item.decay_status}`}>
                                ({item.decay_status === "critical"
                                  ? `${item.days_since_completed}d ago (Urgent)`
                                  : `${item.days_since_completed}d ago`})
                              </span>
                            </div>
                            <div className="kpi-row-line-2">
                              <span className="kpi-row-context">
                                <strong>{item.subject_name}</strong> : {item.topic_name}
                              </span>
                            </div>
                          </div>
                          <button
                            className="kpi-row-action-btn"
                            onClick={() => navigate(ROUTES.SYLLABUS || "/syllabus")}
                            title="Review in Syllabus"
                          >
                            Revise
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab 2: Weak Areas Spotlight Content with Range Benchmark Graph */}
              {activeKpiTab === "weakness" && (
                <div className="kpi-tab-body">
                  {!revisionWeaknessData ? (
                    <div className="dashboard-skeleton dashboard-skeleton-box" style={{ width: "100%", height: "100%", minHeight: "140px", borderRadius: "8px" }} />
                  ) : revisionWeaknessData.weak_areas.total_solved === 0 ? (
                    <div className="dashboard-empty-state-card compact">
                      <CheckCircle2 size={20} className="dashboard-empty-icon text-green" />
                      <span className="dashboard-empty-title">No MCQ Attempts Recorded</span>
                      <span className="dashboard-empty-sub">Attempt prelims practice questions to generate your accuracy benchmark.</span>
                    </div>
                  ) : (
                    <div className="weakness-tab-inner">
                      {/* Benchmark Range Visualizer Bar */}
                      <div className="accuracy-benchmark-card">
                        <div className="benchmark-header-row">
                          <div className="benchmark-title-group">
                            <span className="benchmark-label">YOUR ACCURACY vs EXPECTED RANGE</span>
                            <span className="benchmark-desc">UPSC Prelims Qualifier Zone: 65% – 75%</span>
                          </div>
                          <div className={`benchmark-current-badge ${revisionWeaknessData.weak_areas.overall_accuracy >= 65 ? "green" : revisionWeaknessData.weak_areas.overall_accuracy >= 45 ? "amber" : "red"}`}>
                            <strong>{revisionWeaknessData.weak_areas.overall_accuracy}%</strong> Current
                          </div>
                        </div>

                        <div className="benchmark-bar-track">
                          {/* Shaded Target Range Box */}
                          <div className="benchmark-target-zone" style={{ left: "65%", width: "10%" }}>
                            <span className="benchmark-zone-label">Target (65–75%)</span>
                          </div>

                          {/* User Current Accuracy Pin */}
                          <div
                            className="benchmark-user-pin"
                            style={{ left: `${Math.min(98, Math.max(2, revisionWeaknessData.weak_areas.overall_accuracy))}%` }}
                            title={`Your Accuracy: ${revisionWeaknessData.weak_areas.overall_accuracy}%`}
                          >
                            <div className="pin-head" />
                            <div className="pin-line" />
                          </div>
                        </div>

                        <div className="benchmark-ticks-row">
                          <span>0%</span>
                          <span>25%</span>
                          <span>50%</span>
                          <span className="text-target">65% Target</span>
                          <span>100%</span>
                        </div>
                      </div>

                      {/* Flagged Weak Topics List */}
                      <div className="weak-topics-header-label">
                        <span>WEAKEST TOPICS IDENTIFIED ({revisionWeaknessData.weak_areas.weak_topics?.length || 0})</span>
                      </div>

                      <div className="kpi-items-scrollable">
                        {(revisionWeaknessData.weak_areas.weak_topics || []).map((w) => (
                          <div key={w.topic_id} className="kpi-list-row weakness-row">
                            <div className="kpi-row-info">
                              <div className="kpi-row-line-1">
                                <span className="kpi-row-main-title" title={`${w.subject_name} : ${w.topic_name}`}>
                                  <strong>{w.subject_name}</strong> : {w.topic_name}
                                </span>
                                <span className="kpi-accuracy-chip danger">
                                  {w.accuracy_percent}% accuracy
                                </span>
                              </div>
                              <div className="kpi-row-line-2">
                                <span className="kpi-row-context">
                                  {w.wrong_attempts} mistakes in {w.total_attempts} MCQs
                                  {w.guess_mistakes > 0 && ` (${w.guess_mistakes} on guesses)`}
                                </span>
                              </div>
                            </div>
                            <button
                              className="kpi-row-action-btn practice"
                              onClick={() => {
                                try {
                                  // Pre-set tab and subject/topic filters for McqPractice
                                  sessionStorage.setItem("mcq_default_tab", "pyq");
                                  const existingFilters = JSON.parse(
                                    sessionStorage.getItem("mcq_tab_filters") || "{}"
                                  );
                                  existingFilters["pyq"] = {
                                    subjects: w.subject_name ? [w.subject_name] : [],
                                    exams: [],
                                    topics: w.topic_name
                                      ? [{ topicname: w.topic_name, subject: w.subject_name }]
                                      : [],
                                    theme: [],
                                    months: [],
                                    years: [],
                                  };
                                  sessionStorage.setItem(
                                    "mcq_tab_filters",
                                    JSON.stringify(existingFilters)
                                  );
                                } catch (e) {
                                  console.error("Failed to set practice filter:", e);
                                }
                                navigate(ROUTES.TESTS || "/tests");
                              }}
                              title={`Practice MCQs for ${w.topic_name}`}
                            >
                              Practice
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>


          </div>
        </div>

        {/* ========================================================================= */}
        {/* OBSERVATION (Current State & High-Level Analytics Strip)                  */}
        {/* ========================================================================= */}
        <div className="dashboard-section-block">
          {(() => {
            const isLoading = !healthMetrics;
            const engagement = healthMetrics?.engagement;
            const queriesToday = engagement?.queries_today ?? 0;
            const baselineQueries = engagement?.baseline_avg_30d ?? 0;
            const isEngagementOnTrack = queriesToday >= baselineQueries;

            const dailyArticles = healthMetrics?.daily_articles;
            const articlesReadToday = dailyArticles?.articles_read_today ?? 0;
            const articlesTarget = 8;
            const articlesPending = Math.max(0, articlesTarget - articlesReadToday);
            const articlesPercent = Math.min(100, Math.round((articlesReadToday / articlesTarget) * 100));

            const prelims = healthMetrics?.prelims_readiness;
            const mcqsSolved = prelims?.mcqs_solved_today ?? 0;
            const prelimsAccuracy = prelims?.today_accuracy_percent !== undefined ? Math.round(prelims.today_accuracy_percent) : 0;
            const testsAttempted = prelims?.tests_attempted_today ?? 0;

            const mains = healthMetrics?.mains_readiness;
            const mainsEvals = mains?.total_evaluations ?? 0;
            const lastEvalDateRaw = mains?.last_evaluation_date;
            const formatEvalDate = (dateStr) => {
              if (!dateStr) return null;
              try {
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return dateStr;
                const day = String(d.getDate()).padStart(2, "0");
                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const month = months[d.getMonth()];
                const year = String(d.getFullYear()).slice(-2);
                const hours = String(d.getHours()).padStart(2, "0");
                const minutes = String(d.getMinutes()).padStart(2, "0");
                return `${day}-${month}-${year} ${hours}:${minutes}`;
              } catch {
                return dateStr;
              }
            };
            const formattedEvalDate = formatEvalDate(lastEvalDateRaw);

            return (
              <div className="observation-cards-grid">
                {/* 1. ENGAGEMENT */}
                <div className="obs-card">
                  <div className="obs-header">
                    <span className="obs-title">ENGAGEMENT</span>
                    {isLoading ? (
                      <span className="dashboard-skeleton dashboard-skeleton-pill" />
                    ) : (
                      <span className={`obs-status-pill ${queriesToday === 0 ? "grey" : isEngagementOnTrack ? "green" : "amber"}`}>
                        <TrendingUp size={13} />
                        {queriesToday === 0 ? "No Activity" : isEngagementOnTrack ? "On Track" : "Below Baseline"}
                      </span>
                    )}
                  </div>
                  <div className="obs-body">
                    {isLoading ? (
                      <div className="dashboard-skeleton dashboard-skeleton-title" style={{ width: "70%", height: "24px" }} />
                    ) : (
                      <div className="obs-primary-val">
                        {queriesToday} <span className="obs-unit">QUERIES / DAY</span>
                      </div>
                    )}
                    {isLoading ? (
                      <div className="dashboard-skeleton dashboard-skeleton-text" style={{ width: "85%", marginTop: "4px" }} />
                    ) : (
                      <div className="obs-subtext">
                        {baselineQueries > 0
                          ? `30-day baseline average: ${baselineQueries} queries`
                          : "No query history recorded yet"}
                      </div>
                    )}
                  </div>
                  <div className="obs-footer">
                    <div className="obs-meter-track">
                      <div
                        className={`obs-meter-fill ${queriesToday === 0 ? "grey" : isEngagementOnTrack ? "green" : "amber"}`}
                        style={{ width: `${isLoading ? 0 : Math.min(100, Math.max(queriesToday > 0 ? 10 : 0, Math.round((queriesToday / Math.max(1, baselineQueries)) * 50)))}%` }}
                      ></div>
                    </div>
                    <span className="obs-footer-text">
                      {isLoading ? "Loading activity..." : queriesToday > 0 ? `${(queriesToday * 0.08).toFixed(1)} hrs active today` : "No queries recorded today"}
                    </span>
                  </div>
                </div>

                {/* 2. DAILY ARTICLES */}
                <div className="obs-card">
                  <div className="obs-header">
                    <span className="obs-title">DAILY ARTICLES</span>
                    {isLoading ? (
                      <span className="dashboard-skeleton dashboard-skeleton-pill" />
                    ) : (
                      <span className={`obs-status-pill ${articlesPending === 0 ? "green" : "amber"}`}>
                        <Clock size={13} />
                        {articlesPending === 0 ? "Completed" : `${articlesPending} Pending`}
                      </span>
                    )}
                  </div>
                  <div className="obs-body">
                    {isLoading ? (
                      <div className="dashboard-skeleton dashboard-skeleton-title" style={{ width: "70%", height: "24px" }} />
                    ) : (
                      <div className="obs-primary-val">
                        {articlesReadToday} / {articlesTarget} <span className="obs-unit">ARTICLES READ</span>
                      </div>
                    )}
                    {isLoading ? (
                      <div className="dashboard-skeleton dashboard-skeleton-text" style={{ width: "80%", marginTop: "4px" }} />
                    ) : (
                      <div className="obs-subtext">Target: {articlesTarget} high-yield editorials daily</div>
                    )}
                  </div>
                  <div className="obs-footer">
                    <div className="obs-meter-track">
                      <div className="obs-meter-fill amber" style={{ width: `${isLoading ? 0 : articlesPercent}%` }}></div>
                    </div>
                    <span className="obs-footer-text">
                      {isLoading ? "Loading articles..." : articlesReadToday > 0 ? `${articlesPercent}% completed today` : "No articles read today"}
                    </span>
                  </div>
                </div>

                {/* 3. PRELIMS READINESS */}
                <div className="obs-card">
                  <div className="obs-header">
                    <span className="obs-title">PRELIMS READINESS</span>
                    {isLoading ? (
                      <span className="dashboard-skeleton dashboard-skeleton-pill" />
                    ) : testsAttempted === 0 && mcqsSolved === 0 ? (
                      <span className="obs-status-pill grey">
                        <CheckCircle2 size={13} />
                        Not Attempted
                      </span>
                    ) : (
                      <span className={`obs-status-pill ${prelimsAccuracy >= 60 ? "green" : "amber"}`}>
                        <CheckCircle2 size={13} />
                        {prelimsAccuracy}% Accuracy
                      </span>
                    )}
                  </div>
                  <div className="obs-body">
                    {isLoading ? (
                      <div className="dashboard-skeleton dashboard-skeleton-title" style={{ width: "65%", height: "24px" }} />
                    ) : (
                      <div className="obs-primary-val">
                        {mcqsSolved} <span className="obs-unit">MCQS SOLVED</span>
                      </div>
                    )}
                    {isLoading ? (
                      <div className="dashboard-skeleton dashboard-skeleton-text" style={{ width: "75%", marginTop: "4px" }} />
                    ) : (
                      <div className="obs-subtext">
                        {testsAttempted === 0 && mcqsSolved === 0
                          ? "No sectional tests attempted yet"
                          : `${testsAttempted} sectional test${testsAttempted === 1 ? "" : "s"} attempted`}
                      </div>
                    )}
                  </div>
                  <div className="obs-footer">
                    <div className="obs-meter-track">
                      <div className="obs-meter-fill green" style={{ width: `${isLoading ? 0 : Math.min(100, prelimsAccuracy)}%` }}></div>
                    </div>
                    <span className="obs-footer-text">
                      {isLoading
                        ? "Loading prelims..."
                        : testsAttempted === 0 && mcqsSolved === 0
                          ? "No prelims practice today"
                          : `${testsAttempted} test${testsAttempted === 1 ? "" : "s"} attempted today`}
                    </span>
                  </div>
                </div>

                {/* 4. MAINS READINESS */}
                <div className="obs-card">
                  <div className="obs-header">
                    <span className="obs-title">MAINS READINESS</span>
                    {isLoading ? (
                      <span className="dashboard-skeleton dashboard-skeleton-pill" />
                    ) : (
                      <span className={`obs-status-pill ${mainsEvals > 0 ? "amber" : "grey"}`}>
                        <AlertCircle size={13} />
                        {mainsEvals > 0 ? `${mainsEvals} Evaluated` : "No Evaluations"}
                      </span>
                    )}
                  </div>
                  <div className="obs-body">
                    {isLoading ? (
                      <div className="dashboard-skeleton dashboard-skeleton-title" style={{ width: "65%", height: "24px" }} />
                    ) : (
                      <div className="obs-primary-val">
                        {mainsEvals} <span className="obs-unit">EVALUATIONS</span>
                      </div>
                    )}
                    {isLoading ? (
                      <div className="dashboard-skeleton dashboard-skeleton-text" style={{ width: "80%", marginTop: "4px" }} />
                    ) : (
                      <div className="obs-subtext">
                        {formattedEvalDate
                          ? `Latest: ${formattedEvalDate}`
                          : "No answer copies evaluated yet"}
                      </div>
                    )}
                  </div>
                  <div className="obs-footer">
                    <div className="obs-meter-track">
                      <div className="obs-meter-fill amber" style={{ width: `${isLoading ? 0 : Math.min(100, mainsEvals * 8)}%` }}></div>
                    </div>
                    <span className="obs-footer-text">
                      {isLoading
                        ? "Loading evaluations..."
                        : mainsEvals > 0
                          ? "Mains copy evaluation active"
                          : "No answer copies submitted yet"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ========================================================================= */}
        {/* LAYER 3: 2-COLUMN SPLIT (Column 1: Syllabus Progress | Column 2: Action Tiles) */}
        {/* ========================================================================= */}
        <div className="dashboard-section-block">
          <div
            className="layer-three-split-grid"
            ref={syllabusContainerRef}
            style={{
              gridTemplateColumns: `calc(${syllabusSplitPercent}% - 8px) 16px 1fr`,
            }}
          >

            {/* Column 1: Micro-Deconstructed Syllabus Progress */}
            <div className="syllabus-tracker-card">
              <div className="syllabus-tracker-header">
                <h4 className="card-subhead">Syllabus Progress</h4>
                <button
                  className="btn-view-full-syllabus"
                  onClick={() => navigate(ROUTES.SYLLABUS || "/syllabus")}
                >
                  Full Syllabus &gt;
                </button>
              </div>

              {/* Subject Tabs with Add/Remove capabilities */}
              <div className="subject-pill-tabs">
                {trackedSubjects.map((key) => (
                  <div
                    key={key}
                    className={`subject-tab-item ${selectedSubjectKey === key ? "active" : ""}`}
                    onClick={() => setSelectedSubjectKey(key)}
                  >
                    <span className="subject-tab-title">{key}</span>
                    {trackedSubjects.length > 1 && (
                      <button
                        className="subject-tab-remove-btn"
                        onClick={(e) => handleRemoveSubject(key, e)}
                        title={`Remove ${key} from tracked subjects`}
                      >
                        <X size={10} />
                      </button>
                    )}
                  </div>
                ))}

                {/* Add Subject Dropdown Pill */}
                <div className={`add-subject-select-wrapper ${isMaxSubjectsReached ? "disabled" : ""}`}>
                  <select
                    className="add-subject-select-pill"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleSelectSubject(e.target.value);
                      }
                    }}
                    disabled={isMaxSubjectsReached || availableSubjects.length === 0}
                    title={
                      isMaxSubjectsReached
                        ? "Maximum 5 subjects allowed on dashboard"
                        : "Select additional subject to track"
                    }
                  >
                    <option value="" disabled>
                      {isMaxSubjectsReached
                        ? "Max 5 Added"
                        : availableSubjects.length === 0
                          ? "All Added"
                          : "+ Add Subject"}
                    </option>
                    {availableSubjects.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={11} className="add-subject-select-arrow" />
                </div>
              </div>

              {/* Active Subject High-Level Progress Ring / Split */}
              <div className="subject-summary-banner">
                <div className="subject-summary-left">
                  <span className="subject-paper-tag">{activeSyllabus.paper}</span>
                  <div className="subject-name-big">{activeSyllabus.subject}</div>
                </div>
                <div className="subject-progress-stats">
                  {!syllabusProgressData ? (
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "65px", height: "22px" }} />
                      <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "65px", height: "22px" }} />
                    </div>
                  ) : (
                    <>
                      <div className="stat-chunk">
                        <span className="stat-label">Completed</span>
                        <strong className="stat-value completed">{activeSyllabus.completedPercent}%</strong>
                      </div>
                      <div className="stat-divider">/</div>
                      <div className="stat-chunk">
                        <span className="stat-label">Remaining</span>
                        <strong className="stat-value remaining">{activeSyllabus.remainingPercent}%</strong>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Micro-Deconstructed Subtopics in a balanced grid */}
              <div className="micro-topics-grid">
                {!syllabusProgressData ? (
                  [1, 2, 3, 4, 5, 6].map((idx) => (
                    <div key={`skel_sub_${idx}`} className="micro-topic-item" style={{ pointerEvents: "none" }}>
                      <div className="topic-header-line">
                        <span className="dashboard-skeleton dashboard-skeleton-text" style={{ width: "50%", height: "14px" }} />
                        <div className="topic-metrics-right">
                          <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "55px", height: "14px" }} />
                          <span className="dashboard-skeleton dashboard-skeleton-text" style={{ width: "40px", height: "14px" }} />
                        </div>
                      </div>
                      <div
                        className="dashboard-skeleton dashboard-skeleton-box"
                        style={{ width: "100%", height: "5px", minHeight: "5px", borderRadius: "3px", marginTop: "6px" }}
                      />
                    </div>
                  ))
                ) : activeSyllabus.subtopics.length === 0 ? (
                  <div className="dashboard-empty-strip-msg" style={{ gridColumn: "1 / -1", padding: "18px" }}>
                    <span>No topic progress recorded yet for {activeSyllabus.subject}. Complete study sessions to track progress.</span>
                  </div>
                ) : (
                  activeSyllabus.subtopics.map((sub) => (
                    <div key={sub.id} className="micro-topic-item">
                      <div className="topic-header-line">
                        <div className="topic-name-wrap">
                          <span className="topic-name">{sub.name}</span>
                        </div>
                        <div className="topic-metrics-right">
                          <span className={`topic-status-tag ${sub.status}`}>
                            {sub.statusText}
                          </span>
                        </div>
                      </div>

                      {/* Progress bar with internal text: % done and Started/Completed date */}
                      <div className="segmented-progress-track">
                        <div
                          className={`segmented-fill completed ${sub.status}`}
                          style={{ width: `${Math.max(sub.completed, 0)}%` }}
                        />
                        <div
                          className="segmented-fill remaining"
                          style={{ width: `${Math.max(100 - sub.completed, 0)}%` }}
                        />
                        <div className="segmented-progress-overlay">
                          <span className="bar-percent-text">{sub.completed}% done</span>
                          {sub.isCompleted && sub.completedOn ? (
                            <span className="bar-date-text">Completed on: {sub.completedOn}</span>
                          ) : !sub.isCompleted && (sub.startedOn || sub.completedOn) ? (
                            <span className="bar-date-text">Started on: {sub.startedOn || sub.completedOn}</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Draggable Split Resizer Handle for Layer 3 */}
            <div
              className="split-resizer-handle"
              onMouseDown={handleSyllabusMouseDown}
              title="Click and drag to resize Syllabus Progress"
            >
              <div className="resizer-dots-pill">
                <span className="resizer-dot" />
                <span className="resizer-dot" />
                <span className="resizer-dot" />
              </div>
            </div>

            {/* Column 2: Vertical Stack of 3 Action Cards (Current Affairs, Prelims, Mains) */}
            <div className="action-tiles-vertical-stack">
              {(() => {
                const isMilestonesLoading = !milestonesData;
                const totalArticlesRead = milestonesData?.articles_read_total ?? 0;
                const totalMcqsSolved = milestonesData?.prelims_mcqs_solved_total ?? 0;
                const totalMainsEvaluated = milestonesData?.mains_evaluated_total ?? 0;

                return (
                  <>
                    {/* Card 1: Current Affairs Analysis */}
                    <div className="action-tile-card ca-card">
                      <div className="action-tile-header">
                        <div className="action-tile-title-group">
                          <div className="action-icon-pill orange">
                            <Newspaper size={17} />
                          </div>
                          <h4 className="action-card-title">Current Affairs Analysis</h4>
                        </div>

                        <div className="action-tile-menu-group">
                          {/* Read link to /currentaffairs */}
                          <button
                            className="card-header-link-btn"
                            onClick={() => navigate(ROUTES.CURRENT_AFFAIRS || "/currentaffairs")}
                            title="Read daily current affairs articles"
                          >
                            <span>Read</span>
                            <ExternalLink size={12} />
                          </button>

                          {/* Listen Audio Digest Toggle */}
                          <button
                            className={`card-header-action-btn orange ${showAudio ? "active" : ""}`}
                            onClick={() => setShowAudio((prev) => !prev)}
                            title={showAudio ? "Close Audio Player" : "Listen to Weekly Audio Digest"}
                          >
                            <Headphones size={13} />
                            <span>{showAudio ? "Close" : "Listen"}</span>
                          </button>
                        </div>
                      </div>

                      <div className="action-tile-body">
                        <p className="action-card-desc">
                          Daily editorial breakdown, constitutional angles, and audio synopsis for on-the-go revision.
                        </p>
                        <div className="action-meta-row">
                          {isMilestonesLoading ? (
                            <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "95px", height: "16px" }} />
                          ) : (
                            <span className="action-meta-tag orange">
                              {totalArticlesRead === 0 ? "No articles read yet" : `${totalArticlesRead} read so far`}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Non-disruptive audio player drawer inside the card */}
                      {showAudio && (
                        <div className="ca-player-drawer">
                          <Audioplayer autoPlay onClose={() => setShowAudio(false)} />
                        </div>
                      )}
                    </div>

                    {/* Dynamic order: If upcoming is Prelims then Prelims is 2nd else Mains is 2nd */}
                    {isPrelimsUpcoming ? (
                      <>
                        {/* Card 2: Prelims Mock Practice */}
                        <div className="action-tile-card prelims-card">
                          <div className="action-tile-header">
                            <div className="action-tile-title-group">
                              <div className="action-icon-pill cyan">
                                <Target size={17} />
                              </div>
                              <h4 className="action-card-title">Prelims Mock Practice</h4>
                            </div>

                            <div className="action-tile-menu-group">
                              <button
                                className="card-header-action-btn cyan"
                                onClick={() => navigate(ROUTES.TESTS || "/tests")}
                                title="Start Prelims Test"
                              >
                                <Play size={12} fill="currentColor" />
                                <span>Start Test</span>
                              </button>
                            </div>
                          </div>

                          <div className="action-tile-body">
                            <p className="action-card-desc">
                              Simulate high-yield sectional quizzes, elimination drill sets, and previous year UPSC questions.
                            </p>
                            <div className="action-meta-row">
                              {isMilestonesLoading ? (
                                <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "95px", height: "16px" }} />
                              ) : (
                                <span className="action-meta-tag cyan">
                                  {totalMcqsSolved === 0 ? "No MCQs solved yet" : `${totalMcqsSolved} solved so far`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Card 3: Mains Answer Evaluation */}
                        <div className="action-tile-card mains-card">
                          <div className="action-tile-header">
                            <div className="action-tile-title-group">
                              <div className="action-icon-pill purple">
                                <PenTool size={17} />
                              </div>
                              <h4 className="action-card-title">Mains Answer Evaluation</h4>
                            </div>

                            <div className="action-tile-menu-group">
                              <button
                                className="card-header-action-btn purple"
                                onClick={openUploadModal}
                                title="Upload Answer Copy"
                              >
                                <Upload size={12} />
                                <span>Upload Copy</span>
                              </button>
                            </div>
                          </div>

                          <div className="action-tile-body">
                            <p className="action-card-desc">
                              Upload handwritten GS, Essay, or Optional answers. Receive detailed feedback on structure and rubric scores.
                            </p>
                            <div className="action-meta-row">
                              {isMilestonesLoading ? (
                                <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "105px", height: "16px" }} />
                              ) : (
                                <span className="action-meta-tag purple">
                                  {totalMainsEvaluated === 0 ? "No evaluations yet" : `${totalMainsEvaluated} evaluated so far`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Card 2: Mains Answer Evaluation */}
                        <div className="action-tile-card mains-card">
                          <div className="action-tile-header">
                            <div className="action-tile-title-group">
                              <div className="action-icon-pill purple">
                                <PenTool size={17} />
                              </div>
                              <h4 className="action-card-title">Mains Answer Evaluation</h4>
                            </div>

                            <div className="action-tile-menu-group">
                              <button
                                className="card-header-action-btn purple"
                                onClick={openUploadModal}
                                title="Upload Answer Copy"
                              >
                                <Upload size={12} />
                                <span>Upload Copy</span>
                              </button>
                            </div>
                          </div>

                          <div className="action-tile-body">
                            <p className="action-card-desc">
                              Upload handwritten GS, Essay, or Optional answers. Receive detailed feedback on structure and rubric scores.
                            </p>
                            <div className="action-meta-row">
                              {isMilestonesLoading ? (
                                <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "105px", height: "16px" }} />
                              ) : (
                                <span className="action-meta-tag purple">
                                  {totalMainsEvaluated === 0 ? "No evaluations yet" : `${totalMainsEvaluated} evaluated so far`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Card 3: Prelims Mock Practice */}
                        <div className="action-tile-card prelims-card">
                          <div className="action-tile-header">
                            <div className="action-tile-title-group">
                              <div className="action-icon-pill cyan">
                                <Target size={17} />
                              </div>
                              <h4 className="action-card-title">Prelims Mock Practice</h4>
                            </div>

                            <div className="action-tile-menu-group">
                              <button
                                className="card-header-action-btn cyan"
                                onClick={() => navigate(ROUTES.TESTS || "/tests")}
                                title="Start Prelims Test"
                              >
                                <Play size={12} fill="currentColor" />
                                <span>Start Test</span>
                              </button>
                            </div>
                          </div>

                          <div className="action-tile-body">
                            <p className="action-card-desc">
                              Simulate high-yield sectional quizzes, elimination drill sets, and previous year UPSC questions.
                            </p>
                            <div className="action-meta-row">
                              {isMilestonesLoading ? (
                                <span className="dashboard-skeleton dashboard-skeleton-pill" style={{ width: "95px", height: "16px" }} />
                              ) : (
                                <span className="action-meta-tag cyan">
                                  {totalMcqsSolved === 0 ? "No MCQs solved yet" : `${totalMcqsSolved} solved so far`}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>

          </div>
        </div>

      </div>

      {/* ========================================================================= */}
      {/* MODAL: Answer Copy Upload & Evaluation Selector                           */}
      {/* ========================================================================= */}
      {showUploadModal && (
        <div className="dashboard-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="dashboard-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <FileText size={22} color="#6366f1" />
                <h3 className="dashboard-modal-title">Upload Mains Answer Sheet</h3>
              </div>
              <button className="dashboard-modal-close" onClick={() => setShowUploadModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div
              className="dropzone-box"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={32} color="#0298da" style={{ marginBottom: "8px" }} />
              <div className="dropzone-text-primary">
                {pendingFileName || "Click or Drag & Drop Answer PDF/Images"}
              </div>
              <div className="dropzone-text-sub">
                {pendingFile ? "Click to pick a different file" : "Supports GS 1-4, Essay, and Optional handwritten copies"}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                style={{ display: "none" }}
                onChange={(e) => {
                  handleEvalFile(e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </div>

            <textarea
              placeholder="Enter the question"
              value={evalQuestion}
              onChange={(e) => setEvalQuestion(e.target.value)}
              rows="3"
              className="modal-question-input"
            />

            <div className="modal-form-group">
              <label className="modal-label">Select Paper</label>
              <div className="paper-buttons-row">
                {Object.keys(evalMarksOptions).map((subject) => (
                  <button
                    key={subject}
                    type="button"
                    onClick={() => {
                      setEvalSubject(subject);
                      setEvalMarks(evalMarksOptions[subject][0]);
                    }}
                    className={`modal-chip-btn ${evalSubject === subject ? "selected" : ""}`}
                  >
                    {subject}
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-form-group">
              <label className="modal-label">Select Marks</label>
              <div className="paper-buttons-row">
                {evalMarksOptions[evalSubject].map((mark) => (
                  <button
                    key={mark}
                    type="button"
                    onClick={() => setEvalMarks(mark)}
                    className={`modal-chip-btn ${evalMarks === mark ? "selected" : ""}`}
                  >
                    {mark} Marks
                  </button>
                ))}
              </div>
            </div>

            <div className="modal-footer-actions">
              <button
                className="btn-modal-cancel"
                onClick={() => setShowUploadModal(false)}
              >
                Cancel
              </button>
              <button
                disabled={!pendingFile || !evalQuestion?.trim()}
                onClick={handleEvaluateClick}
                className="btn-modal-submit"
              >
                Start Evaluation
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Promotional Side Drawer: Course Discount / New Program Launch */}
      <PromoDrawer />

      {/* Floating Action Dock: Telegram Community & WhatsApp Mentor Support */}
      <div className="dashboard-floating-dock" aria-label="Support and Community">
        {/* Telegram Community */}
        <div className="floating-dock-item">
          <span className="floating-dock-tooltip">Join Us</span>
          <a
            href="https://t.me/askyooki"
            target="_blank"
            rel="noopener noreferrer"
            className="floating-dock-btn"
            title="Join Us"
            aria-label="Join Us on Telegram (t.me/askyooki)"
          >
            <img
              src="/assets/images/telegram.svg"
              alt="Telegram"
              className="floating-dock-icon"
            />
          </a>
        </div>

        {/* WhatsApp Mentor Support */}
        <div className="floating-dock-item">
          <span className="floating-dock-tooltip">
            Want to talk to a mentor or report an issue?
          </span>
          <a
            href={`https://wa.me/919560581662?text=${encodeURIComponent("Hi Mentor, I want to talk to a mentor or report an issue.")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="floating-dock-btn"
            title="Want to talk to a mentor or report an issue?"
            aria-label="Want to talk to a mentor or report an issue?"
          >
            <img
              src="/assets/images/whatsapp.svg"
              alt="WhatsApp"
              className="floating-dock-icon"
            />
          </a>
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
